import React, { Ref as ReactRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';

import List from 'components/common/List';

import { setVideoIsClicked } from 'actions/clickedVideo';
import {
  addRotatingSegmentTitleId,
  deleteRotatingSegmentTitleId,
} from 'actions/rotatingSegmentTitles';

import { useIntl } from 'react-intl';

import {
  VideoCtvRedux,
  KitchenSinkRowItem,
  KitcenSinkRowItemDataItem,
  RelatedItem,
  TileItem,
} from 'store-types';
import { Game } from 'app-types';

import HomeTile from './HomeTile';

const messages = {
  ttsEndOfNavigation: {
    id: 'navigation.tts.ttsEndOfNavigation',
    description: 'TTS text for navigation command cannot be executed',
  },
};

export interface TileClickProps {
  rowId: string;
  video?: TileItem | null;
  rowSlug?: string;
}

interface HomeRowProps {
  row: KitchenSinkRowItem;
  displayTitle?: boolean;
  displayScoring?: boolean;
  games?: Game[] | null;
  onTileClick?: (props: TileClickProps) => void;
  rowClassName?: string | null;
  titleClassName?: string | null;
  tileClassName?: string | null;
  forwardedRef?: ReactRef<unknown> | null;
  focusIdPrefix?: string | null;
  rowPos?: number | null;
  onEndOfNavigationOverride?: null | ((index: number) => void);
}

const HomeRow = ({
  row,
  displayTitle,
  displayScoring = false,
  games = null,
  onTileClick = () => {},
  rowClassName = null,
  titleClassName = null,
  tileClassName = null,
  forwardedRef = null,
  focusIdPrefix = '',
  rowPos = null,
  onEndOfNavigationOverride = null,
}: HomeRowProps) => {
  const dispatch = useDispatch();

  const ttsEndOfNavigation = useIntl().formatMessage(
    messages.ttsEndOfNavigation,
  );

  const rotatingSegmentTitles = useSelector(
    (state: VideoCtvRedux) => state.rotatingSegmentTitles,
  );

  const rotatingSegmentIds = useSelector(
    (state: VideoCtvRedux) => state.updateRotatingSegmentTitles?.rotatingSegmentIds,
  );

  const segmentTitlesEnabled = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data?.config?.modules?.SegmentTitles?.enabled,
  );

  const renderItem = (item: KitcenSinkRowItemDataItem | RelatedItem, index: number, ref?: React.RefObject<HTMLDivElement>) => {
    let shortTitle: string | null | undefined = item?.about?.shortTitle;
    if (segmentTitlesEnabled && rotatingSegmentTitles?.data?.titleData) {
      if (
        Object.keys(rotatingSegmentTitles.data.titleData).find(
          key => key === item?.id,
        ) !== undefined
      ) {
        shortTitle =
          rotatingSegmentTitles?.data?.titleData[item?.id].segmentTitle !== null
            ? rotatingSegmentTitles?.data?.titleData[item?.id].segmentTitle
            : shortTitle;
        if (rotatingSegmentIds && rotatingSegmentIds.indexOf(item?.id) === -1) {
          dispatch(addRotatingSegmentTitleId(item?.id));
        }
      } else if (
        rotatingSegmentIds &&
        rotatingSegmentIds.indexOf(item?.id) !== -1
      ) {
        dispatch(deleteRotatingSegmentTitleId(item?.id));
      }
    }
    const updatedItem = { ...item, about: { ...item.about, shortTitle } } as TileItem;

    return (
      <HomeTile
        focusIdPrefix={focusIdPrefix}
        item={updatedItem}
        row={row}
        displayScoring={displayScoring}
        games={games}
        onClick={(tileClickProps: TileClickProps) => {
          dispatch(
            setVideoIsClicked(updatedItem.id, row.title, rowPos, index + 1),
          );
          onTileClick?.(tileClickProps);
        }}
        ref={ref}
        key={updatedItem.id || `${row.slug}-${index}`}
        className={tileClassName}
      />
    );
  };

  return (
    <>
      {displayTitle && (
        <div className={classNames(titleClassName, 'font-weight-bold')}>
          {row.title}
        </div>
      )}
      <List
        focusId={row.slug}
        data={row.data}
        buffer={row.layoutType}
        className={rowClassName}
        renderItem={renderItem}
        peekRatio={0}
        restorationId={`${row.slug}`}
        ref={forwardedRef}
        ttsEndOfNavigation={ttsEndOfNavigation}
        onEndOfNavigationOverride={onEndOfNavigationOverride}
      />
    </>
  );
};

export default React.memo(
  React.forwardRef<unknown, Omit<HomeRowProps, 'forwardedRef'>>((props, ref) => <HomeRow {...props} forwardedRef={ref} />),
);
